'use strict';

// PDP add-on box (SFCC-3229), rendered by Product-Addon (product/components/pdpAddon.isml).

const cartUtil = require('theme/js/cart/util');

const ADDON_SELECTOR = '[data-pdp-addon]';
const TOGGLE_SELECTOR = '[data-pdp-addon-toggle]';
const DISMISSED_KEY_PREFIX = 'pdpAddonDismissed-';

// The add-on is in the cart or the shopper closed the box.
let isAddonRemoved = false;
// The last add to cart request included the add-on.
let isAddonRequested = false;

/**
 * @param {jQuery} $addon - add-on row
 * @param {boolean} isVisible
 */
function toggleAddon($addon, isVisible) {
    $addon.toggleClass('hide', !isVisible);
    if (!isVisible) {
        $addon.find(TOGGLE_SELECTOR).prop('checked', false);
    }
}

/**
 * @param {Array} items - cart model items
 * @param {string} pid
 * @returns {boolean}
 */
function containsProduct(items, pid) {
    return Array.isArray(items) && items.some((item) => item.id === pid);
}

/**
 * Cart items stored by the cart widget, if they belong to the current session.
 * @returns {Array|null}
 */
function getStoredCartItems() {
    try {
        const cartModel = JSON.parse(localStorage.getItem('cart'));
        if (cartModel && cartModel.sessionID == cartUtil.getSessionID()) { // eslint-disable-line eqeqeq
            return cartModel.items;
        }
    } catch (e) {
        // No usable cart model, the server skips the add-on if it is already in the basket.
    }

    return null;
}

/**
 * @param {string} pid - add-on product ID
 * @returns {boolean} true if the shopper closed the box during this browser session
 */
function isDismissed(pid) {
    try {
        return sessionStorage.getItem(DISMISSED_KEY_PREFIX + pid) === '1';
    } catch (e) {
        return false;
    }
}

/**
 * @param {string} pid - add-on product ID
 */
function setDismissed(pid) {
    try {
        sessionStorage.setItem(DISMISSED_KEY_PREFIX + pid, '1');
    } catch (e) {
        // Storage not available (e.g. private mode): the box is only closed on this page.
    }
}

/**
 * Same check as product/components/promotionBoxes.isml.
 * @param {Array} promotions - promotions of the product model
 * @returns {boolean}
 */
function hasPromotionBox(promotions) {
    return Array.isArray(promotions) && promotions.some((promotion) => promotion.details
        && promotion.details.indexOf('save-more') === -1);
}

/**
 * The box is cached separately from the PDP and its server-side check (pdpAddonHelpers.js) only uses the active
 * customer promotions, so promotion boxes that depend on the shopper's basket or Voyado profile are checked here.
 * @param {jQuery} $addon - add-on row
 * @returns {boolean} true if the PDP shows a promotion box (product/components/promotionBoxes.isml)
 */
function isPromotionBoxShown($addon) {
    const $details = $addon.closest('.product-detail').find('.promotion-details');
    return $details.length > 0 && $details.text().trim() !== '';
}

/**
 * Pushes the add to cart event of the add-on, with the price the basket charges.
 * @param {jQuery} $addon - add-on row
 */
function trackAddonAddToCart($addon) {
    const trackingNode = $addon.find('.pdp-addon-tracking').get(0);

    if (!window.dataLayer || !trackingNode || !trackingNode.dataset.trackingClickEvent || !trackingNode.dataset.trackingEventPayload) {
        return;
    }

    const payload = JSON.parse(trackingNode.dataset.trackingEventPayload);
    const salesPrice = $addon.attr('data-addon-sales-price');
    const trackedProduct = payload && payload.add && payload.add.products && payload.add.products[0];

    if (trackedProduct && salesPrice) {
        trackedProduct.price = typeof trackedProduct.price === 'string' ? Number(salesPrice).toFixed(2) : Number(salesPrice);
    }

    window.dataLayer.push({
        event: trackingNode.dataset.trackingClickEvent,
        ecommerce: payload
    });
}

module.exports = {
    hideIfInCartOrDismissed: function () {
        const $addon = $(ADDON_SELECTOR);
        const pid = $addon.attr('data-addon-pid');

        if ($addon.length && (isDismissed(pid) || containsProduct(getStoredCartItems(), pid))) {
            isAddonRemoved = true;
            toggleAddon($addon, false);
        } else if ($addon.length && isPromotionBoxShown($addon)) {
            // Not isAddonRemoved: a variant without a promotion box shows the box again (afterAttributeSelect).
            toggleAddon($addon, false);
        }
    },

    close: function () {
        $(document).on('click', '[data-pdp-addon-close]', function () {
            const $addon = $(this).closest(ADDON_SELECTOR);

            setDismissed($addon.attr('data-addon-pid'));
            isAddonRemoved = true;
            toggleAddon($addon, false);
            // The close button disappears, so move focus to the add to cart button above the box.
            $addon.closest('.product-detail').find('button.add-to-cart').first().trigger('focus');
        });
    },

    openQuickViewFromImage: function () {
        // The image link is hidden from assistive tech, so the quickview remembers the name link and returns focus there on close.
        $(document).on('click', '[data-pdp-addon-image]', function (e) {
            e.preventDefault();
            $(this).closest(ADDON_SELECTOR).find('.pdp-addon-name').trigger('click');
        });
    },

    selectFromQuickView: function () {
        // The add-on price needs the main product in the basket, so the popup selects the add-on instead of adding it.
        $(document).on('click', '[data-pdp-addon-select]', function () {
            const $toggle = $(ADDON_SELECTOR).find(TOGGLE_SELECTOR);

            $toggle.prop('checked', true);
            $('#quickViewModal .quickview-modal-close-button').trigger('click');
            $toggle.trigger('focus');
        });
    },

    // The shared quickview modal lets Tab move out of it and loses focus on a backdrop click: handled for the add-on popup only.
    quickViewFocus: function () {
        const isAddonQuickView = ($modal) => $modal.find('.product-quickview--addon').length > 0;

        $(document).on('keydown', '#quickViewModal', function (e) {
            if (e.key !== 'Tab' || !isAddonQuickView($(this))) {
                return;
            }

            const $focusable = $(this).find('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, iframe, [tabindex]:not([tabindex="-1"])').filter(':visible');
            const first = $focusable.get(0);
            const last = $focusable.get(-1);

            if (e.shiftKey && (document.activeElement === first || document.activeElement === this)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        });

        $(document).on('hidden.bs.modal', '#quickViewModal', function () {
            const focused = document.activeElement;

            if (isAddonQuickView($(this)) && (!focused || focused === document.body || this.contains(focused))) {
                $(ADDON_SELECTOR).find('.pdp-addon-name').trigger('focus');
            }
        });
    },

    // On mobile the add-on popup is a bottom sheet (product/_pdpAddonQuickView.scss): dragging it down from the top closes it.
    // The close button stays the non-gesture alternative.
    quickViewSwipeToClose: function () {
        const CONTENT_SELECTOR = '#quickViewModal .quickview-modal-content';
        const CLOSE_DISTANCE = 100;
        let start = null;
        let deltaY = 0;

        $(document).on('touchstart', CONTENT_SELECTOR, function (e) {
            const body = $(this).find('.quickview-modal-body').get(0);
            const isBottomSheet = window.matchMedia('(max-width: 767px)').matches
                && $(this).find('.product-quickview--addon').length > 0;

            // Only from the top of the sheet, so scrolling its content up still works.
            start = isBottomSheet && (!body || body.scrollTop <= 0) ? e.originalEvent.touches[0] : null;
            deltaY = 0;
        });

        $(document).on('touchmove', CONTENT_SELECTOR, function (e) {
            if (!start) {
                return;
            }

            const touch = e.originalEvent.touches[0];
            const moveX = touch.clientX - start.clientX;
            const moveY = touch.clientY - start.clientY;

            // Horizontal swipes belong to the image carousel.
            if (!deltaY && Math.abs(moveX) > Math.abs(moveY)) {
                start = null;
                return;
            }

            deltaY = Math.max(0, moveY);
            this.style.transition = 'none';
            this.style.transform = deltaY ? 'translateY(' + deltaY + 'px)' : '';
        });

        $(document).on('touchend touchcancel', CONTENT_SELECTOR, function () {
            if (!start) {
                return;
            }

            start = null;
            this.style.transition = '';

            if (deltaY > CLOSE_DISTANCE) {
                $('#quickViewModal .quickview-modal-close-button').trigger('click');
            } else {
                this.style.transform = '';
            }
        });
    },

    addAddonToForm: function () {
        $(document).on('updateAddToCartFormData', 'button.add-to-cart', function (e, form) {
            const $addon = $(this).closest('.product-detail').find(ADDON_SELECTOR);

            isAddonRequested = $addon.length > 0 && !$addon.hasClass('hide') && $addon.find(TOGGLE_SELECTOR).is(':checked');
            if (isAddonRequested) {
                form.addonPid = $addon.attr('data-addon-pid'); // eslint-disable-line no-param-reassign
            }
        });
    },

    afterAddToCart: function () {
        $('body').on('product:afterAddToCart', function (e, data) {
            const $addon = $(ADDON_SELECTOR);

            if (!$addon.length || !data) {
                return;
            }

            if (data.addonIsAdded) {
                trackAddonAddToCart($addon);
            }

            // Hidden once the add-on is in the cart, and also when it was requested but could not be added
            // (e.g. sold out since the box was cached), so it is not offered again for a request that fails.
            const isInCart = data.cart && containsProduct(data.cart.items, $addon.attr('data-addon-pid'));
            if (isInCart || (isAddonRequested && data.productIsAdded && !data.addonIsAdded)) {
                isAddonRemoved = true;
                toggleAddon($addon, false);
            }
            isAddonRequested = false;
        });
    },

    afterAttributeSelect: function () {
        $('body').on('product:afterAttributeSelect', function (e, response) {
            const $addon = $(ADDON_SELECTOR);

            if (!$addon.length || !response || !response.data || !response.data.product
                || response.container.closest('.quick-view-dialog').length) {
                return;
            }

            const promotions = response.data.product.promotions;
            toggleAddon($addon, !isAddonRemoved && !hasPromotionBox(promotions));
        });
    }
};
